import { useMemo, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'

import { apiGetJob } from '@/features/jobs/api/api-get-job'
import { JobDetailCard } from '@/features/jobs/components/job-detail-card'
import { JobPhotosSection } from '@/features/jobs/components/job-photos-section'
import { SignalResultValue } from '@/features/jobs/components/signal-result-value'
import { formatAbortedReason } from '@/features/jobs/lib/format-aborted-reason'
import { formatAntennaType } from '@/features/jobs/lib/format-antenna-type'
import { formatDateRaised } from '@/features/jobs/lib/format-date-raised'
import { formatDateTime } from '@/features/jobs/lib/format-date-time'
import { formatFullAddress } from '@/features/jobs/lib/format-full-address'
import { formatJobOutcomeType } from '@/features/jobs/lib/format-job-outcome-type'
import { formatJobType } from '@/features/jobs/lib/format-job-type'
import { isJobSubmitted } from '@/features/jobs/lib/is-job-submitted'
import type {
  AntennaSignalResult,
  JobDetail,
  JobPhoto,
  JobPhotoType,
} from '@/features/jobs/types/job-detail'
import { DetailField, LoadingSpinner } from '@/shared/components'
import { useApiResource } from '@/shared/hooks/use-api-resource'
import { MapPin } from 'lucide-react'

const displayValue = (value: string | null | undefined) => value?.trim() || ''

const photosOfType = (photos: JobPhoto[], photoType: JobPhotoType) =>
  photos.filter((photo) => photo.photo_type === photoType)

const signalResultForSequence = (
  job: JobDetail,
  sequence: number
): AntennaSignalResult | null => {
  const reading = job.antenna_test_readings.find(
    (item) => item.test_sequence === sequence
  )

  return reading?.signal_result ?? null
}

const signalResultValue = (job: JobDetail, sequence: number): ReactNode => {
  const signalResult = signalResultForSequence(job, sequence)

  return signalResult ? <SignalResultValue signalResult={signalResult} /> : ''
}

export const JobShowPage = () => {
  const { id } = useParams<{ id: string }>()
  const loadJob = useMemo(() => (id ? () => apiGetJob(id) : null), [id])
  const jobResource = useApiResource(loadJob, 'Failed to load job')

  const job = jobResource.data
  const isLoading = jobResource.isLoading
  const error = id ? jobResource.error : 'Job not found'

  if (isLoading) {
    return <LoadingSpinner position="center" label="Loading job" />
  }

  if (error) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {error}
      </p>
    )
  }

  if (!job) {
    return null
  }

  const fullAddress = formatFullAddress(job.location)
  const showOutcomeDetails = isJobSubmitted(job.status)
  const showRemediationReason = job.job_type === 'remediation'
  const isAborted = job.status === 'aborted'
  const barrierPhotos = photosOfType(job.job_photos, 'barrier')
  const installationPhotos = photosOfType(job.job_photos, 'installation')
  const abortEvidencePhotos = photosOfType(job.job_photos, 'abort_evidence')
  const hasPhotoSections =
    barrierPhotos.length > 0 ||
    installationPhotos.length > 0 ||
    isAborted

  return (
    <div className="flex flex-col gap-2">
      <header className="mt-4 p-2">
        <h2 className="text-2xl font-bold text-primary-dark">
          Job {job.location.location_id}
        </h2>
      </header>

      <hr className="border-outline/40" />

      <div className="flex flex-col gap-6 p-4">
        <div className="grid gap-6 lg:grid-cols-2">
          <JobDetailCard title="Job Information">
            <dl className="flex flex-col gap-4">
              <DetailField
                label="Date raised"
                value={formatDateRaised(job.date_raised)}
              />
              <DetailField label="Job type" value={formatJobType(job.job_type)} />
              <DetailField
                label="Device attached"
                value={
                  job.device_attached ? (
                    <Link
                      to={`/devices/${job.device_attached.id}`}
                      className="font-semibold text-primary underline"
                    >
                      {job.device_attached.serial_number}
                    </Link>
                  ) : (
                    ''
                  )
                }
              />
              {showRemediationReason &&
                <DetailField
                  label="Reason for remediation"
                  value={displayValue(job.reason_for_remediation)}
                  multiline
                />
              }
            </dl>
          </JobDetailCard>

          <JobDetailCard title="Location Details">
            <dl className="flex flex-col gap-4">
              <DetailField
                label="Latitude"
                value={displayValue(job.location.latitude)}
              />
              <DetailField
                label="Longitude"
                value={displayValue(job.location.longitude)}
              />
              <DetailField
                label="Asset location"
                value={displayValue(job.location.asset_location)}
              />
              <DetailField
                label="Road name"
                value={displayValue(job.location.road_name)}
              />
              <DetailField
                label="Town"
                value={displayValue(job.location.town)}
              />
              <DetailField
                label="County"
                value={displayValue(job.location.county)}
              />
            </dl>
          </JobDetailCard>
        </div>

        <hr className="mt-2 border-outline/40" />

        <section className="flex flex-col gap-3">
          <div className="flex justify-between gap-4">
            <h3 className="text-base font-semibold text-foreground">Location</h3>
            <p
              className="flex items-center text-right text-sm text-foreground"
              aria-label="Full address"
            >
              <MapPin size={18} />
              {fullAddress || '—'}
            </p>
          </div>
          <div
            aria-label="Map"
            className="flex min-h-64 items-center justify-center rounded-xl border border-outline/40 bg-surface-muted/40 text-sm text-muted-foreground"
          >
            Map placeholder
          </div>
        </section>

        {showOutcomeDetails ? (
          <>
            <div className="grid gap-6 lg:grid-cols-2">
              <JobDetailCard title="Antenna">
                <dl className="flex flex-col gap-4">
                  <DetailField
                    label="Type"
                    value={
                      job.job_outcome?.antenna_type
                        ? formatAntennaType(job.job_outcome.antenna_type)
                        : ''
                    }
                  />
                  <DetailField
                    label="Signal test 1"
                    value={signalResultValue(job, 1)}
                  />
                  <DetailField
                    label="Signal test 2"
                    value={signalResultValue(job, 2)}
                  />
                  <DetailField
                    label="Signal test 3"
                    value={signalResultValue(job, 3)}
                  />
                </dl>
              </JobDetailCard>

              <JobDetailCard title="Outcome Details">
                <dl className="flex flex-col gap-4">
                  <DetailField
                    label="Outcome"
                    value={
                      job.job_outcome
                        ? formatJobOutcomeType(job.job_outcome.outcome)
                        : ''
                    }
                  />
                  {isAborted ? (
                    <DetailField
                      label="Abort reason"
                      value={
                        job.job_outcome?.aborted_reason
                          ? formatAbortedReason(job.job_outcome.aborted_reason)
                          : ''
                      }
                    />
                  ) : null}
                  <DetailField
                    label="Completed at"
                    value={
                      job.job_outcome
                        ? formatDateTime(job.job_outcome.completed_at)
                        : ''
                    }
                  />
                  <DetailField
                    label="Completed by"
                    value={displayValue(job.job_outcome?.completed_by_name)}
                  />
                  <DetailField
                    label="Notes"
                    value={displayValue(job.job_outcome?.notes)}
                    multiline
                  />
                </dl>
              </JobDetailCard>
            </div>

            {hasPhotoSections ? (
              <>
                <hr className="mt-2 border-outline/40" />

                <div className="grid gap-6 lg:grid-cols-2">
                  {barrierPhotos.length > 0 ? (
                    <JobPhotosSection
                      title="Barrier Photos"
                      ariaLabel="Barrier photos"
                      photos={barrierPhotos}
                    />
                  ) : null}

                  {installationPhotos.length > 0 ? (
                    <JobPhotosSection
                      title="Installation Photos"
                      ariaLabel="Installation photos"
                      photos={installationPhotos}
                    />
                  ) : null}

                  {isAborted ? (
                    <JobPhotosSection
                      title="Abort Evidence Photos"
                      ariaLabel="Abort evidence photos"
                      photos={abortEvidencePhotos}
                      emptyMessage="No abort evidence photos available"
                    />
                  ) : null}
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}
